import {
  cn,
  Loader,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarInset,
  SidebarProvider,
  SidebarRail,
} from "@devhop/ui";
import { Outlet, useNavigate } from "@tanstack/react-router";
import {
  CalendarDaysIcon,
  GraduationCapIcon,
  ListChecks,
  StarIcon,
  WalletIcon,
} from "lucide-react";
import { useEffect, useMemo } from "react";
import { useAuthState } from "@/modules/auth/presentation/model/useAuthState";
import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import { countDocumentAttention } from "@/modules/performance/domain/services/document-due-urgency";
import { useUnconfirmedClassEnrollmentCountQuery } from "@/modules/class-enrollments/presentation/api/queries";
import { useDocumentsQuery, useEventTeamAssignmentWarningsCountQuery } from "@/modules/performance/presentation/api/queries";
import type { PermissionId } from "@/modules/roles/domain/contracts/permissions";
import { getCookie } from "@/shared/lib/cookies";
import { SkipToMain } from "@/shared/ui/SkipToMain";
import {
  type ActiveSystem,
  LayoutProvider,
  useLayout,
} from "../providers/LayoutProvider";
import { AppSidebar } from "./AppSidebar";
import {
  eventsSidebarData,
  expensesSidebarData,
  formsSidebarData,
  performanceSidebarData,
  sidebarData,
} from "./data/sidebar-data";
import { NavGroup } from "./NavGroup";
import { NavUser } from "./NavUser";
import { TeamSwitcher, type TeamSwitcherTeam } from "./TeamSwitcher";
import type { NavGroup as NavGroupType, NavItem } from "./types";

const FORMS_ACCESS_PERMISSIONS: PermissionId[] = [
  "forms:create",
  "forms:read",
  "forms:update",
  "forms:delete",
  "audience-vote:read",
  "audience-vote:write",
  "audience-vote:export",
];

const EXPENSES_ACCESS_PERMISSIONS: PermissionId[] = [
  "expenses:create",
  "expenses:read",
  "expenses:update",
  "expenses:approve",
  "expenses:pay",
  "expenses:void",
  "expenses:manage-categories",
];

const EVENTS_ACCESS_PERMISSIONS: PermissionId[] = [
  "camp-events:read",
  "camp-registrations:read",
  "jam-events:read",
  "jam-registrations:read",
  "workshop-registrations:read",
  "workshop-events:read",
  "visitor-lottery:read",
  "visitor-lottery:write",
];

type WorkspaceTeam = TeamSwitcherTeam & {
  system: ActiveSystem;
};

const TEAMS_BY_SYSTEM: Record<ActiveSystem, WorkspaceTeam> = {
  studio: {
    name: "Fanglao Studio",
    logo: GraduationCapIcon,
    plan: "ຈັດການຮຽນ / ສຕູດິໂອ",
    system: "studio",
  },
  expenses: {
    name: "Expenses",
    logo: WalletIcon,
    plan: "ບັນທຶກຄ່າໃຊ້ຈ່າຍອົງກອນ",
    system: "expenses",
  },
  events: {
    name: "Events & Registration",
    logo: CalendarDaysIcon,
    plan: "Camp / Jam / Workshop / Lottery",
    system: "events",
  },
  performance: {
    name: "Performance Show",
    logo: StarIcon,
    plan: "ຈັດການງານສະແດງ",
    system: "performance",
  },
  forms: {
    name: "Form Builder",
    logo: ListChecks,
    plan: "ຟອມ ແລະ ການລົງທະບຽນ",
    system: "forms",
  },
};

const ALL_WORKSPACE_TEAMS = Object.values(TEAMS_BY_SYSTEM);

const DASHBOARD_BY_SYSTEM: Record<ActiveSystem, string> = {
  studio: "/app/dashboard",
  expenses: "/app/expenses/overview",
  events: "/app/camp-events",
  performance: "/app/performance/dashboard",
  forms: "/app/forms/dashboard",
};

function withPerformanceNavBadges(
  groups: NavGroupType[],
  badges: Array<{
    url: string;
    badge: string;
    badgeTone?: "default" | "warning" | "danger";
  }>,
): NavGroupType[] {
  if (badges.length === 0) return groups;

  const mapItems = (items: NavItem[]): NavItem[] =>
    items.map((item) => {
      if ("url" in item) {
        const match = badges.find((entry) => entry.url === item.url);
        if (match) {
          return {
            ...item,
            badge: match.badge,
            badgeTone: match.badgeTone,
          };
        }
      }
      if ("items" in item && item.items) {
        return { ...item, items: mapItems(item.items) };
      }
      return item;
    });

  return groups.map((group) => ({
    ...group,
    items: mapItems(group.items),
  }));
}

function withDocumentsAttentionBadge(
  groups: NavGroupType[],
  badge: string | undefined,
  badgeTone: "warning" | "danger" | undefined,
): NavGroupType[] {
  if (!badge) return groups;
  return withPerformanceNavBadges(groups, [
    {
      url: "/app/performance/documents",
      badge,
      badgeTone,
    },
  ]);
}

function AppSidebarContent() {
  const { activeSystem, setActiveSystem } = useLayout();
  const navigate = useNavigate({ from: "/app" });
  const { permissions } = usePermissions();
  const documentsQuery = useDocumentsQuery({
    enabled: activeSystem === "performance",
  });
  const teamWarningsQuery = useEventTeamAssignmentWarningsCountQuery({
    enabled: activeSystem === "performance",
  });
  const documentsAttention = useMemo(
    () => countDocumentAttention(documentsQuery.data ?? []),
    [documentsQuery.data],
  );
  const teamAssignmentWarningCount = teamWarningsQuery.data?.count ?? 0;
  const canReadClassEnrollments = permissions.includes("class-enrollments:read");
  const unconfirmedEnrollmentsQuery = useUnconfirmedClassEnrollmentCountQuery({
    enabled: activeSystem === "studio" && canReadClassEnrollments,
  });
  const unconfirmedEnrollmentCount = unconfirmedEnrollmentsQuery.data?.count ?? 0;

  const visibleTeams = useMemo(() => {
    return ALL_WORKSPACE_TEAMS.filter((t) => {
      if (t.system === "forms") {
        return FORMS_ACCESS_PERMISSIONS.some((p) => permissions.includes(p));
      }
      if (t.system === "events") {
        return EVENTS_ACCESS_PERMISSIONS.some((p) => permissions.includes(p));
      }
      if (t.system === "expenses") {
        return EXPENSES_ACCESS_PERMISSIONS.some((p) =>
          permissions.includes(p),
        );
      }
      return true;
    });
  }, [permissions]);

  useEffect(() => {
    if (!visibleTeams.some((t) => t.system === activeSystem)) {
      setActiveSystem(visibleTeams[0]?.system ?? "studio");
    }
  }, [activeSystem, setActiveSystem, visibleTeams]);

  const activeTeam =
    visibleTeams.find((t) => t.system === activeSystem) ??
    visibleTeams[0] ??
    TEAMS_BY_SYSTEM.studio;

  const activeNavGroups = useMemo(() => {
    const base =
      activeSystem === "performance"
        ? performanceSidebarData.navGroups
        : activeSystem === "forms"
          ? formsSidebarData.navGroups
          : activeSystem === "events"
            ? eventsSidebarData.navGroups
            : activeSystem === "expenses"
              ? expensesSidebarData.navGroups
              : sidebarData.navGroups;

    if (activeSystem === "studio") {
      if (unconfirmedEnrollmentCount <= 0) return base;
      return withPerformanceNavBadges(base, [
        {
          url: "/app/class-enrollments",
          badge: String(unconfirmedEnrollmentCount),
          badgeTone: "warning",
        },
      ]);
    }

    if (activeSystem !== "performance") {
      return base;
    }

    let groups = base;
    if (documentsAttention.total > 0) {
      groups = withDocumentsAttentionBadge(
        groups,
        String(documentsAttention.total),
        documentsAttention.overdue > 0 ? "danger" : "warning",
      );
    }
    if (teamAssignmentWarningCount > 0) {
      groups = withPerformanceNavBadges(groups, [
        {
          url: "/app/performance/events",
          badge: String(teamAssignmentWarningCount),
          badgeTone: "warning",
        },
      ]);
    }
    return groups;
  }, [activeSystem, documentsAttention, teamAssignmentWarningCount, unconfirmedEnrollmentCount]);

  const handleTeamChange = (team: TeamSwitcherTeam) => {
    const system = (team as WorkspaceTeam).system;
    if (system === activeSystem) return;
    setActiveSystem(system);
    void navigate({ to: DASHBOARD_BY_SYSTEM[system] });
  };

  return (
    <AppSidebar>
      <SidebarHeader>
        <TeamSwitcher
          teams={visibleTeams}
          activeTeam={activeTeam}
          onTeamChange={handleTeamChange}
        />
      </SidebarHeader>
      <SidebarContent>
        {activeNavGroups.map((props) => (
          <NavGroup key={props.title} {...props} />
        ))}
      </SidebarContent>
      <SidebarFooter>
        <NavUser />
      </SidebarFooter>
      <SidebarRail />
    </AppSidebar>
  );
}

type AuthenticatedLayoutProps = {
  children?: React.ReactNode;
};

export function AuthenticatedLayout({ children }: AuthenticatedLayoutProps) {
  const defaultOpen = getCookie("sidebar_state") !== "false";
  const { isLoading, isAuthenticated } = useAuthState();
  const navigate = useNavigate({ from: "/app" });

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate({ to: "/auth/login" });
    }
  }, [isLoading, isAuthenticated, navigate]);

  if (isLoading) {
    return (
      <div className="container grid h-svh max-w-none items-center justify-center">
        <Loader />
      </div>
    );
  }

  return (
    <SidebarProvider defaultOpen={defaultOpen}>
      <LayoutProvider>
        <SkipToMain />
        <AppSidebarContent />
        <SidebarInset
          className={cn(
            "has-data-[layout=fixed]:h-svh",
            "peer-data-[variant=inset]:has-data-[layout=fixed]:h-[calc(100svh-(var(--spacing)*4))]",
            "@container/content",
          )}
        >
          {children ?? <Outlet />}
        </SidebarInset>
      </LayoutProvider>
    </SidebarProvider>
  );
}
